// Centralized URL routing for the BARK SPA. (BAR-40)
//
// The app navigates between its top-level views. Historically the active
// view lived in a single `mainView` useState in App.tsx and the browser
// URL never changed. This module makes the URL the single source of
// truth: a `NavState` is the canonical description of "where the user
// is", and the two pure functions below map it to/from a URL.
//
// Everything here is framework-agnostic and pure so it can be unit
// tested in isolation (routes.test.ts) and reused by the
// `useUrlNavigation` hook that bridges it into React Router.
import { classificationSelection } from '../classification/navigation';

/** The top-level views the SPA can render. Grew out of the union that
 *  used to live on `mainView` in App.tsx. */
export type MainView =
  | 'landing'
  | 'projects'
  | 'new-project'
  | 'workspace'
  | 'logic-chain'
  | 'build-artifacts'
  | 'quick-research'
  | 'lookup'
  | 'settings'
  | 'billing'
  | 'admin'
  | 'inventory'
  | 'institution'
  | 'reporting-preview'
  | 'classification'
  | 'connect';

export type ProjectType = 'research' | 'build';
export type ProjectsFilter = 'all' | 'research' | 'build';

/** The canonical, URL-encodable description of the current location.
 *  Only fields that genuinely identify "where the user is" live here —
 *  in-project session state (open tab, build phase, selected node,
 *  chat history) is owned by the Zustand stores and is intentionally
 *  NOT encoded in the URL. */
export interface NavState {
  view: MainView;
  /** Project id for the project-scoped views (workspace / logic-chain /
   *  build-artifacts). Undefined for the research-workspace and
   *  build-artifacts drafts (no project yet). */
  projectId?: string;
  /** `/projects` type filter. Defaults to 'all'. */
  projectsFilter?: ProjectsFilter;
  /** `/projects/new` form variant. Defaults to 'research'. */
  newProjectType?: ProjectType;
  /** `/quick-research` prefill query. */
  quickQuery?: string;
  /** Preserved Classification document/deal selection; never trusted tenant identity. */
  classificationSelection?: string;
  classificationShare?: string;
}

/** Path constants / patterns. Used by `ShellRoutes`' `<Route path=...>` table
 *  (and available for `useMatch`); the mapping functions below remain the
 *  runtime source of truth for NavState<->URL conversion. */
export const ROUTE_PATTERNS = {
  landing: '/',
  projects: '/projects',
  newProject: '/projects/new',
  quickResearch: '/quick-research',
  lookup: '/lookup',
  settings: '/settings',
  billing: '/billing',
  admin: '/admin',
  inventory: '/inventory',
  institution: '/institution',
  reportingPreview: '/reporting/preview',
  classification: '/classification',
  /** How to connect BARK's MCP server to an AI assistant. Open to every
   *  signed-in account, and where a free (MCP-only) account lands. Not the
   *  BAR-962 `?connect=<state>` query, which is a connector sign-in on any path. */
  connect: '/connect',
  workspace: '/research/:projectId',
  logicChain: '/research/:projectId/logic-chain',
  buildArtifacts: '/build/:projectId',
  /** Sentinel segment for the research-workspace draft (no project yet). */
  researchDraft: '/research/new',
  /** Sentinel segment for the build-artifacts draft (no project yet). */
  buildDraft: '/build/new',
} as const;

const PROJECT_TYPES: ProjectType[] = ['research', 'build'];
const PROJECTS_FILTERS: ProjectsFilter[] = ['all', 'research', 'build'];

function asProjectType(v: string | null): ProjectType | undefined {
  return v && (PROJECT_TYPES as string[]).includes(v) ? (v as ProjectType) : undefined;
}

function asProjectsFilter(v: string | null): ProjectsFilter | undefined {
  return v && (PROJECTS_FILTERS as string[]).includes(v) ? (v as ProjectsFilter) : undefined;
}

// Decode a path segment, tolerating a malformed `%` sequence (a hand-crafted
// bad URL) by returning it verbatim rather than throwing.
function safeDecode(seg: string): string {
  try {
    return decodeURIComponent(seg);
  } catch {
    return seg;
  }
}

/**
 * Map a NavState to a URL path (pathname + optional search). The mapping
 * is canonical and idempotent: feeding the output back through
 * `pathToNavState` yields an equivalent NavState, and re-encoding that
 * yields the same string. Default values (projectsFilter='all',
 * newProjectType='research', empty quickQuery) are omitted from the URL
 * so shared links stay clean.
 */
export function navStateToPath(state: NavState): string {
  const { view, projectId, projectsFilter, newProjectType, quickQuery } = state;

  switch (view) {
    case 'landing':
      return ROUTE_PATTERNS.landing;

    case 'projects':
      // 'all' is the default and is omitted; research/build are explicit.
      return projectsFilter && projectsFilter !== 'all'
        ? `${ROUTE_PATTERNS.projects}?type=${projectsFilter}`
        : ROUTE_PATTERNS.projects;

    case 'new-project':
      // 'research' is the default and is omitted; 'build' is explicit.
      return newProjectType && newProjectType !== 'research'
        ? `${ROUTE_PATTERNS.newProject}?type=${newProjectType}`
        : ROUTE_PATTERNS.newProject;

    case 'quick-research':
      return quickQuery
        ? `${ROUTE_PATTERNS.quickResearch}?q=${encodeURIComponent(quickQuery)}`
        : ROUTE_PATTERNS.quickResearch;

    case 'lookup':
      return ROUTE_PATTERNS.lookup;

    case 'settings':
      return ROUTE_PATTERNS.settings;
    case 'billing':
      return ROUTE_PATTERNS.billing;

    case 'admin':
      return ROUTE_PATTERNS.admin;

    case 'inventory':
      return ROUTE_PATTERNS.inventory;

    case 'institution':
      return ROUTE_PATTERNS.institution;
    case 'reporting-preview':
      return ROUTE_PATTERNS.reportingPreview;
    case 'classification': {
      const params = new URLSearchParams(classificationSelection(state.classificationSelection || ''));
      if (state.classificationShare && /^cs_[a-f0-9]{32}$/.test(state.classificationShare)) params.set('shared_deal', state.classificationShare);
      return (projectId ? `/classification/${encodeURIComponent(projectId)}` : ROUTE_PATTERNS.classification)
        + (params.toString() ? `?${params}` : '');
    }

    case 'connect':
      return ROUTE_PATTERNS.connect;

    case 'workspace':
      // Workspace can render before a project exists (sidebar "Regulatory
      // Research" lands in an empty draft canvas); the draft maps to the
      // reserved /research/new path, symmetric with build-artifacts below.
      // Project ids are encoded (symmetric with the parse-side decode) so an
      // id containing a URL-significant char (/, ?, #) can't corrupt the path.
      return projectId ? `/research/${encodeURIComponent(projectId)}` : ROUTE_PATTERNS.researchDraft;

    case 'logic-chain':
      return projectId ? `/research/${encodeURIComponent(projectId)}/logic-chain` : ROUTE_PATTERNS.landing;

    case 'build-artifacts':
      // Build-artifacts can render before a project exists (box-press
      // draft); the draft maps to the reserved /build/new path.
      return projectId ? `/build/${encodeURIComponent(projectId)}` : ROUTE_PATTERNS.buildDraft;

    default:
      return ROUTE_PATTERNS.landing;
  }
}

/**
 * Parse a URL (pathname + search) into a NavState. Unknown / malformed
 * paths resolve to the landing view so the app always has a valid view
 * to render (the caller normalizes the URL to '/' for unknown paths).
 */
export function pathToNavState(pathname: string, search = ''): NavState {
  const params = new URLSearchParams(search);
  // Split and drop empty segments so '/projects/' === '/projects'.
  const seg = pathname.split('/').filter(Boolean);

  if (seg.length === 0) {
    return { view: 'landing' };
  }

  switch (seg[0]) {
    case 'projects':
      if (seg[1] === 'new') {
        return { view: 'new-project', newProjectType: asProjectType(params.get('type')) ?? 'research' };
      }
      if (seg.length === 1) {
        return { view: 'projects', projectsFilter: asProjectsFilter(params.get('type')) ?? 'all' };
      }
      return { view: 'landing' };

    case 'lookup':
      // `/lookup/<chatId>` is one saved chat; LookupRoute reads the id.
      return seg.length <= 2 ? { view: 'lookup' } : { view: 'landing' };

    case 'quick-research': {
      if (seg.length !== 1) return { view: 'landing' };
      const q = params.get('q');
      return q ? { view: 'quick-research', quickQuery: q } : { view: 'quick-research' };
    }

    case 'settings':
      return seg.length === 1 ? { view: 'settings' } : { view: 'landing' };

    case 'billing':
      return seg.length === 1 ? { view: 'billing' } : { view: 'landing' };

    case 'admin':
      return seg.length === 1 ? { view: 'admin' } : { view: 'landing' };

    case 'inventory':
      return seg.length === 1 ? { view: 'inventory' } : { view: 'landing' };

    case 'institution':
      return seg.length === 1 ? { view: 'institution' } : { view: 'landing' };
    case 'reporting':
      return seg.length === 2 && seg[1] === 'preview' ? { view: 'reporting-preview' } : { view: 'landing' };
    case 'classification':
      return seg.length <= 2 ? {
        view: 'classification',
        ...(params.getAll('shared_deal').length === 1 && /^cs_[a-f0-9]{32}$/.test(params.get('shared_deal') || '') ? { classificationShare: params.get('shared_deal')! } : {}),
        ...(seg[1] ? { projectId: safeDecode(seg[1]) } : {}),
        ...(classificationSelection(search) ? { classificationSelection: classificationSelection(search) } : {}),
      } : { view: 'landing' };

    case 'connect':
      return seg.length === 1 ? { view: 'connect' } : { view: 'landing' };

    case 'research': {
      if (!seg[1]) return { view: 'landing' };
      // /research/new => research draft (no project yet), mirrors /build/new.
      // Checked before treating seg[1] as a project id; backend project ids
      // are generated and never the literal 'new', so there's no collision.
      if (seg[1] === 'new' && seg.length === 2) {
        return { view: 'workspace' };
      }
      const projectId = safeDecode(seg[1]);
      if (seg[2] === 'logic-chain' && seg.length === 3) {
        return { view: 'logic-chain', projectId };
      }
      if (seg.length === 2) {
        return { view: 'workspace', projectId };
      }
      return { view: 'landing' };
    }

    case 'build': {
      // /build/new => draft (no project yet). Bare /build (no segment) is not
      // a real route — fall through to landing, consistent with other views.
      if (seg[1] === 'new') {
        return { view: 'build-artifacts' };
      }
      if (seg.length === 2 && seg[1]) {
        return { view: 'build-artifacts', projectId: safeDecode(seg[1]) };
      }
      return { view: 'landing' };
    }

    default:
      return { view: 'landing' };
  }
}

/** Convenience: true for the views that require an active project. */
export function isProjectScopedView(view: MainView): boolean {
  return view === 'workspace' || view === 'logic-chain' || view === 'build-artifacts';
}

/** URL parameters that mean another flow is on screen: a connector sign-in
 *  (BAR-962) or an activation, reset or invite link. App strips each one from
 *  the address bar once it has read it, so the auth state is what lasts. */
const LANDING_FLOW_PARAMS = ['connect', 'signup_token', 'reset_token', 'invite_token'];

export interface LandingRedirectInput {
  /** `isMcpOnlyCustomer`: every web module off, `mcp_access` on. */
  mcpOnly: boolean;
  /** A `?connect=` connector sign-in is waiting for consent. */
  connectPending?: boolean;
  signupToken?: string | null;
  resetToken?: string | null;
  inviteToken?: string | null;
  /** The current `location.search`. */
  search?: string;
}

/**
 * Whether Home (`/`) sends this account to the connect page instead.
 *
 * A free (MCP-only) account has no door to open on Home: its product works in
 * an AI assistant, so `/connect` is its home. A web account is never sent
 * there. Nor is anyone while another flow owns the screen: the consent for a
 * pending `?connect=` sign-in, or an activation, reset or invite link.
 */
export function landingRedirectsToConnect(input: LandingRedirectInput): boolean {
  if (!input.mcpOnly) return false;
  if (input.connectPending || input.signupToken || input.resetToken || input.inviteToken) return false;
  const params = new URLSearchParams(input.search || '');
  return !LANDING_FLOW_PARAMS.some((name) => params.has(name));
}
